import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, ShieldCheck, FilePlus, Sparkles } from "lucide-react";

export default function NewShipment() {
  const nav = useNavigate();
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Default `start_stage` is left null and computed at submit-time based on the
  // selected buyer's `is_established` flag and the operator's "Sign new SLA?" toggle.
  const [form, setForm] = useState({
    buyer_id: "",
    reference: "",
    product: "Avocado",
    variety: "",
    notes: "",
  });
  // For established buyers — does the operator want to sign a fresh SLA for this
  // shipment? When true the shipment starts at the SLA stage; otherwise it starts
  // at Sales Confirmation, using the existing master agreement.
  const [signNewSla, setSignNewSla] = useState(false);

  useEffect(() => {
    api.get("/buyers").then((r) => {
      setBuyers(r.data);
      if (r.data.length) setForm((f) => ({ ...f, buyer_id: r.data[0].id }));
    }).finally(() => setLoading(false));
  }, []);

  const selectedBuyer = useMemo(
    () => buyers.find((b) => b.id === form.buyer_id),
    [buyers, form.buyer_id],
  );
  const isEstablished = !!selectedBuyer?.is_established;

  const startStage = !isEstablished ? "buyer" : (signNewSla ? "sla" : "sales_confirmation");
  const startStageLabel = {
    buyer: "Buyer Registration (Step 1 of 7)",
    sla: "Service Agreement (Step 2 of 7)",
    sales_confirmation: "Sales Confirmation (Step 3 of 7)",
  }[startStage];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_id) {
      toast.error("Please register and select a buyer first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shipments", { ...form, start_stage: startStage });
      const msg = startStage === "sales_confirmation"
        ? "Shipment created — starting at Sales Confirmation"
        : startStage === "sla"
          ? "Shipment created — upload the new SLA for owner approval"
          : "Shipment created — begin Buyer Registration";
      toast.success(msg);
      nav(`/app/shipments/${data.id}`, { replace: true });
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 space-y-5">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#6B0F1A]" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-400 font-bold">{startStageLabel}</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#1F1F1F] mt-1">New Shipment</h1>
        <p className="text-stone-500 text-sm mt-1">Select a buyer and start the export pipeline.</p>
      </div>

      {loading ? (
        <div className="text-sm text-stone-400">Loading buyers…</div>
      ) : buyers.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-8 text-center space-y-3">
          <UserPlus className="w-8 h-8 mx-auto text-stone-300" />
          <div className="font-semibold text-[#1F1F1F]">No buyers registered</div>
          <div className="text-sm text-stone-500">Register a buyer before creating a shipment.</div>
          <button
            onClick={() => nav("/app/buyers/new")}
            className="bg-[#6B0F1A] text-white rounded-xl py-2.5 px-4 font-semibold hover:bg-[#56121C]"
            data-testid="go-register-buyer"
          >
            Register buyer
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 bg-white border border-stone-200 rounded-2xl p-5" data-testid="new-shipment-form">
          <Field label="Buyer">
            <select
              value={form.buyer_id}
              onChange={(e) => { setForm({ ...form, buyer_id: e.target.value }); setSignNewSla(false); }}
              className="input"
              data-testid="buyer-select"
            >
              {buyers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.company_name} — {b.country}{b.is_established ? "  ✓ Established" : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => nav("/app/buyers/new")}
              className="text-xs font-semibold text-[#6B0F1A] mt-2"
              data-testid="add-new-buyer-link"
            >
              + Add new buyer
            </button>
          </Field>

          {/* Established-buyer fast-track block. Lets the operator decide whether to
              re-sign the SLA or use the existing master agreement. */}
          {isEstablished && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3" data-testid="established-buyer-block">
              <div className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-emerald-900">{selectedBuyer.company_name} is an established buyer.</span>
                  <span className="text-emerald-800/80"> KYC and master SLA are already on file — you can skip those stages.</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setSignNewSla(false)}
                  className={`flex items-start gap-2.5 text-left rounded-lg border-2 px-3 py-2.5 transition ${!signNewSla ? "bg-white border-emerald-600 shadow-sm" : "border-stone-200 hover:border-stone-300 bg-white/40"}`}
                  data-testid="use-master-sla-option"
                >
                  <ShieldCheck className={`w-4 h-4 mt-0.5 shrink-0 ${!signNewSla ? "text-emerald-700" : "text-stone-400"}`} />
                  <div>
                    <div className="text-sm font-semibold text-[#1F1F1F]">Use master SLA</div>
                    <div className="text-[11px] text-stone-500 leading-snug">Start at Sales Confirmation</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSignNewSla(true)}
                  className={`flex items-start gap-2.5 text-left rounded-lg border-2 px-3 py-2.5 transition ${signNewSla ? "bg-white border-[#6B0F1A] shadow-sm" : "border-stone-200 hover:border-stone-300 bg-white/40"}`}
                  data-testid="sign-new-sla-option"
                >
                  <FilePlus className={`w-4 h-4 mt-0.5 shrink-0 ${signNewSla ? "text-[#6B0F1A]" : "text-stone-400"}`} />
                  <div>
                    <div className="text-sm font-semibold text-[#1F1F1F]">Sign a new SLA</div>
                    <div className="text-[11px] text-stone-500 leading-snug">Upload + owner approval, then continue</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          <Field label="Reference (optional)">
            <input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="input"
              placeholder="Auto-generated if blank"
              data-testid="reference-input"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product">
              <select value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="input" data-testid="product-input">
                {["Avocado", "Coffee", "Tea", "Pulses", "Grains", "Oilseeds", "Nuts", "Meat", "Fresh Produce", "Private Label"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Variety">
              <input value={form.variety} onChange={(e) => setForm({ ...form, variety: e.target.value })} className="input" placeholder="e.g. Hass, Arabica" data-testid="variety-input" />
            </Field>
          </div>
          <Field label="Notes">
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid="notes-input" />
          </Field>
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#6B0F1A] hover:bg-[#56121C] text-white font-semibold rounded-xl py-3 disabled:opacity-60 active:scale-[0.98]"
            data-testid="create-shipment-submit"
          >
            {busy ? "Creating…"
              : startStage === "sales_confirmation" ? "Create shipment & open Sales Confirmation"
              : startStage === "sla" ? "Create shipment & upload new SLA"
              : "Create shipment & begin Buyer Registration"}
          </button>
        </form>
      )}

      <style>{`.input { width:100%; background:#FBFAF9; border:1px solid #E5E0DC; border-radius:.5rem; padding:.75rem 1rem; color:#1F1F1F; outline:none; } .input:focus { background:white; border-color:#A8323F; box-shadow:0 0 0 3px rgba(168,50,63,0.15);} `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
